<template>
  <div
    class="layout"
    :class="[computedCharacterColorClass]"
  >
    <!-- 梵字・家紋 + 戒名 + 位表記 -->
    <div
      class="main"
      :class="{
        adjust: isSymbolAvailable,
        other: underCharacters.length,
      }"
    >
      <!-- 梵字・家紋 -->
      <div>
        <div
          v-if="isSymbolAvailable"
          class="symbol flex center"
        >
          <div class="box">
            <ImageResolver
              :src="computedSymbolSrc"
              class="character"
            />
          </div>
        </div>
      </div>

      <!-- スペーサー -->
      <div />

      <!-- 戒名 -->
      <div class="buddhistNames">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`buddhistName-${column.order}`"
          class="buddhistName flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 戒名 -->
          <TabletCharacterImage
            v-for="(character, index) in column.buddhistName"
            :key="`character-${character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...buddhistNamePayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- スペーサー (0px or 15px) -->
      <div />

      <!-- 位表記 -->
      <div class="underCharacters">
        <!-- 位表記 > 霊ごと -->
        <template v-if="underCharactersPlacement === 'EACH_COLUMN'">
          <div
            v-for="target in ['left', 'right']"
            :key="`under-character-${target}`"
            class="underCharacter flex center column"
            :class="{
              left: target === 'left',
              right: target === 'right',
            }"
          >
            <TabletCharacterImage
              v-for="(character, index) in underCharacters"
              :key="`under-character-${character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...underCharactersPayload,
                characterIndex: index,
                columnIndex: null,
                ...$event,
              })"
            />
          </div>
        </template>

        <template v-if="underCharactersPlacement === 'CENTER'">
          <div class="underCharacter middle flex center column">
            <TabletCharacterImage
              v-for="(character, index) in underCharacters"
              :key="`under-character-${character}-${index}`"
              :char="character"
              :font="typography.font"
              :size="computedCharacterSize"
              @onClickCharacter="onClickCharacter({
                ...underCharactersPayload,
                characterIndex: index,
                columnIndex: null,
                ...$event,
              })"
            />
          </div>
        </template>
      </div>

      <!-- スペーサー -->
      <div />
    </div>

    <!-- 没年月日 -->
    <div class="side">
      <!-- スペーサー -->
      <div />

      <!-- 没年 + 没月 + 没日 -->
      <div class="dates">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`date-${column.order}`"
          class="date flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 没年 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathYear"
            :key="`deathYear-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathYearPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />

           <!-- 没月 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathMonth"
            :key="`deathMonth-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathMonthPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />

          <!-- 没日 -->
          <TabletCharacterImage
            v-for="(character, index) in column.dateOfDeathDay"
            :key="`deathDay-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...dateOfDeathDayPayload,
              characterIndex: index,
              columnIndex: columnIndex,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- 没表記 -->
      <div class="diedCharacters">
        <div
          v-for="column in columns"
          :key="`diedCharacter-${column.order}`"
          class="diedCharacter flex align-center column justify-space-between"
          :class="{
            left: column.order === 2,
            right: column.order === 1,
          }"
        >
          <!-- 没表記 -->
          <TabletCharacterImage
            v-for="(character, index) in diedCharacter"
            :key="`diedCharacter-${character.character}-${index}`"
            :char="character"
            :font="typography.font"
            :size="computedSideCharacterSize"
            @onClickCharacter="onClickCharacter({
              ...diedCharacterPayload,
              characterIndex: index,
              columnIndex: null,
              ...$event,
            })"
          />
        </div>
      </div>

      <!-- スペーサー -->
      <div />
    </div>
  </div>
</template>

<script>
import TabletPreviewMixin from "@/mixins/tabletPreview.mixin";

export default {
  mixins: [
    TabletPreviewMixin.front,
    TabletPreviewMixin.character,
  ],
  components: {
  },
  props: {
  },
  mounted () {},
  data () {
    return {
    };
  },
  computed: {
    // 梵字・家紋 + 戒名 + 位表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedCharacterSize () {
      const {
        isSymbolAvailable,
        columns,
        underCharacters,
      } = this;

      const defaultSize = 40;
      const gap = underCharacters.length ? 10 : 0;
      const maxHeight = !isSymbolAvailable ? 400 : 320 - 15 - gap;

      const rightCharacterLength = columns[0].buddhistName.length + underCharacters.length;
      const leftCharacterLength = columns[1].buddhistName.length + underCharacters.length;
      const characterLength = Math.max(rightCharacterLength, leftCharacterLength);
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },

    // 没年月日 + 没表記
    // の文字列の長さから要素一つあたりのサイズを算出
    computedSideCharacterSize () {
      const {
        columns,
        diedCharacter,
      } = this;

      const defaultSize = 20;
      const maxHeight = 400;

      const rightDateOfDeathLength = columns[0].dateOfDeath.length || 0;
      const leftDateOfDeathLength = columns[1].dateOfDeath.length || 0;
      const rightCharacterLength = rightDateOfDeathLength + diedCharacter.length;
      const leftCharacterLength = leftDateOfDeathLength + diedCharacter.length;
      const characterLength = Math.max(rightCharacterLength, leftCharacterLength);
      const characterSize = maxHeight / characterLength;
      const size = Math.min(defaultSize, characterSize);

      return size;
    },
  },
  watch: {},
  destroyed () {},
  methods: {},
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";
@import "~@/assets/styles/tablet-front.scss";

.layout {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .main {
    display: grid;
    grid-template-rows: auto 1fr auto 0px auto 1fr;
    grid-template-columns: 1fr;
    height: 100%;
    width: 80px;
    position: absolute;
    top: 0;
    left: -80px;
    box-sizing: border-box;
    overflow-x: visible;
    z-index: 1;

    &.adjust {
      padding-bottom: 15px; // (80 - 50) / 2
    }

    &.other {
      grid-template-rows: auto 1fr auto 10px auto 1fr;
    }

    @include symbol(115px);  // (150 + 80) / 2

    .buddhistNames {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .buddhistName {
        height: 100%;
        width: 40px;

        &.left {
          float: left;
          transform: translateX(115px);
        }

        &.right {
          float: right;
          transform: translateX(115px);
        }

        @include character(40px);
      }
    }

    .underCharacters {
      height: 100%;
      width: 100%;
      position: relative;
      box-sizing: border-box;

      .underCharacter {
        height: 100%;
        width: 40px;

        &.middle {
          transform: translateX(135px);
        }

        &.left {
          float: left;
          transform: translateX(115px);
        }

        &.right {
          float: right;
          transform: translateX(115px);
        }

        @include character(40px);
      }
    }
  }

  .side {
    display: grid;
    grid-template-rows: 1fr auto auto 1fr;
    grid-template-columns: 1fr;
    height: 100%;
    position: relative;
    box-sizing: border-box;
    padding: 0 2.5px;

    .dates,
    .diedCharacters {
      height: 100%;
      width: 100%;
      position: relative;

      &.adjust {
        padding-top: 15px;
      }

      .date,
      .diedCharacter {
        height: 100%;
        width: 35px;

        &.left {
          float: left;
        }

        &.right {
          float: right;
          transform: translateX(0px);
        }

        @include character(20px);
      }
    }
  }
}
</style>
